import type { Ref } from 'vue';
import {
  computed,
} from 'vue';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { IAffiliateProfileForm } from '@modules/profile-settings/types';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import {
  TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS,
} from '@modules/profile-settings/constants';
import {
  useAffiliateProfileQuery,
} from '@modules/profile-settings/queries';
import { utcDateToLocal } from '@utils/date';

export interface AffiliateGeneralProfileSettingsComposable {
  affiliateSchema: Ref<FormSchema<IAffiliateProfileForm>>;
  isLoadingAffiliateData: Ref<boolean>;
}

export default function useAffiliateGeneralProfileSettings(): AffiliateGeneralProfileSettingsComposable {
  const { getTranslation } = useI18n();

  const { data: profile, isLoading: isLoadingAffiliateData } = useAffiliateProfileQuery();

  const affiliateSchema = computed<FormSchema<IAffiliateProfileForm>>(() => ({
    name: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'MAIN_NAME').value,
      value: profile.value?.name,
    },
    surname: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'MAIN_SURNAME').value,
      value: profile.value?.surname,
    },
    username: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'MAIN_USERNAME').value,
      value: profile.value?.username,
      readonly: true,
    },
    telegram: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'MAIN_TELEGRAM').value,
      value: profile.value?.telegram,
    },
    ms_teams: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'MAIN_MS_TEAMS').value,
      value: profile.value?.ms_teams,
    },
    whatsapp: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'MAIN_WHATSAPP').value,
      value: profile.value?.whatsapp,
    },
    birthday: {
      type: EFormInputTypes.DATE,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'MAIN_BIRTHDAY').value,
      value: profile.value?.birthday ? utcDateToLocal(profile.value?.birthday) : undefined,
      readonly: true,
    },
    phone_number: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'MAIN_PHONE_NUMBER').value,
      value: profile.value?.phone_number,
    },
    site_url: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'MAIN_SITE_URL').value,
      value: profile.value?.site_url,
    },
    about_affiliate: {
      type: EFormInputTypes.TEXTAREA,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'MAIN_ABOUT_AFFILIATE').value,
      value: profile.value?.about_affiliate,
    },
    agree_receive_emails: {
      type: EFormInputTypes.CHECKBOX,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'MAIN_AGREE_RECEIVE_EMAILS').value,
      value: profile.value?.agree_receive_emails,
    },
  }));

  return {
    affiliateSchema,
    isLoadingAffiliateData,
  };
}
